/**
 * MontaTech v2 - Constants
 */

import type { MontaTechProfile } from "./types";
import { getTenantIdForWebSocket } from "../../../utils/getTenantId";

// ==================== ROTATION OPTIONS ====================

export const ROTATION_OPTIONS = [
	{ value: 1, label: "0°" },
	{ value: 32, label: "5°" },
	{ value: 8, label: "45°" },
	{ value: 4, label: "90°" },
	{ value: 2, label: "180°" },
] as const;

export const getRotationLabel = (value: number): string => {
	const opt = ROTATION_OPTIONS.find((o) => o.value === value);
	return opt?.label || "0°";
};

// ==================== STORAGE ====================

export const STORAGE_KEYS = {
	LOCAL_PROFILES: "montatech:local-profiles",
	LAST_PROFILE: "montatech:last-profile",
	OTSTUP_MIGRATED: "montatech:otstup-migrated-v1",
} as const;

// ==================== API ====================

// Função para obter tenantId dinâmico (usa VITE_TENANT_ID em dev, subdomínio em prod)
// ✅ CORRIGIDO: Retorna string conforme documentação (NÃO número)
function getMontaTechTenantId(): string {
	// getTenantIdForWebSocket retorna string ('dev', 'one1print', 'one1printdf', etc)
	// Backend espera STRING, não número (conforme documentação)
	return getTenantIdForWebSocket();
}

// Função para obter endpoint de profiles com tenantId dinâmico
export function getProfilesEndpoint(): string {
	const tenantId = getMontaTechTenantId();
	let num = 0
	switch (tenantId) {
		case 'one1print':
			num = 1;
			break;
		case 'oneuniformes':
			num = 1;

			break
		default:
			break;
	}
	return `/plotcalc/config?tenantId=${num}`;
}

export const API_ENDPOINTS = {
	// PROFILES agora é função para usar tenantId dinâmico
	get PROFILES() {
		return getProfilesEndpoint();
	},
	SHAPE_COLLECTOR: "/tools/shapecollector",
} as const;

export const API_CACHE_TTL = {
	PROFILES: 5 * 60 * 1000, // 5 minutos
	SHAPE_COLLECTOR: 20 * 60 * 1000, // 20 minutos
} as const;

// ==================== TIMEOUTS ====================

export const TIMEOUTS = {
	COLLECT: 60 * 60 * 1000, // 60 minutos para coleta
	CALCULATE: 60 * 60 * 1000, // 60 minutos para cálculo
} as const;

// ==================== DEFAULT PROFILE ====================

export const DEFAULT_PROFILE: MontaTechProfile = {
	name: "Novo Perfil",
	isLocal: true,
	materialWith: 1570000,
	materialHeight: 4500000,
	leftField: 10000,
	rightField: 10000,
	upField: 10000,
	downField: 10000,
	otstup: 10000,
	gradusCount: 4,
	vnutri_Figuri: false,
	vlogen: false,
	group: true,
	lockObj: false,
	drawText: true,
	drawFields: true,
	drawWorkObl: true,
	drawMaterial: true,
	plotCalcLayer: true,
	plotterSheetWith: 1570000,
	plotterSheetHeight: 4500000,
	blocksPerRow: 3,
};

// ==================== GRID LAYOUT OPTIONS ====================

export const BLOCKS_PER_ROW_OPTIONS = [
	{ value: 1, label: "1" },
	{ value: 2, label: "2" },
	{ value: 3, label: "3" },
	{ value: 4, label: "4" },
	{ value: 5, label: "5" },
] as const;

export const DEFAULT_BLOCKS_PER_ROW = 3;

// ==================== PROFILE OPTIONS ====================

export const PROFILE_BOOLEAN_OPTIONS = {
	encaixe: [
		{ key: "vnutri_Figuri", label: "Encaixar dentro de figuras vazadas" },
		{ key: "vlogen", label: "Modo encaixado" },
		{ key: "group", label: "Agrupar objetos" },
		{ key: "lockObj", label: "Bloquear objetos" },
	],
	visualizacao: [
		{ key: "drawText", label: "Mostrar texto" },
		{ key: "drawFields", label: "Mostrar campos" },
		{ key: "drawWorkObl", label: "Mostrar área de trabalho" },
		{ key: "drawMaterial", label: "Mostrar material" },
		{ key: "plotCalcLayer", label: "Criar layer MontaTech" },
	],
} as const;

export const MARGIN_FIELDS = [
	{ key: "leftField", label: "Esq" },
	{ key: "rightField", label: "Dir" },
	{ key: "upField", label: "Sup" },
	{ key: "downField", label: "Inf" },
] as const;

// ==================== UTILS ====================

export const micronsToMm = (microns: number): number => microns / 1000;
export const mmToMicrons = (mm: number): number => mm * 1000;
